import { useRef, ChangeEvent } from 'react';
import { Button, ButtonProps } from '@chakra-ui/button';
import { PlusSquareIcon } from '@chakra-ui/icons';
import { InputProps, Input } from '@chakra-ui/input';
import { Image } from '@chakra-ui/image';
import { useToast } from '@chakra-ui/toast';

type InputFileButtonProps = Omit<ButtonProps, 'onChange'> & {
  alt?: string;
  label?: string;
  iconSrc?: string;
  onClick?: () => void;
  isDisabled?: boolean;
  onChange?: InputProps['onChange'];
  mimeTypes?: Array<string>;
  maxSize?: number; // in KB
  maxAttachment?: number;
  isAttachmentFull?: boolean;
  multiple?: boolean;
};

const isFileTypeValid = (accepts: Array<string>, file: File) => {
  return accepts.indexOf(file.type) >= 0;
};

const isMaxFileSize = (maxSize: number, file: File) => {
  return file.size > maxSize * 1024;
};

const getUnit = (size: number) => {
  const sizeMB = size / 1024;
  return sizeMB < 1
    ? { size: size, unit: 'KB' }
    : { size: sizeMB.toFixed(2), unit: 'MB' };
};

export function InputFileButton({
  alt,
  label,
  iconSrc,
  onClick,
  isDisabled,
  onChange,
  mimeTypes,
  maxSize,
  maxAttachment,
  isAttachmentFull,
  multiple = false,
  ...props
}: InputFileButtonProps) {
  const inputFileRef = useRef<HTMLInputElement>(null);
  const toast = useToast();

  const onChangeInput = (event: ChangeEvent<HTMLInputElement>) => {
    if (event.target.files && event.target.files[0]) {
      const file = event.target.files[0];
      if (mimeTypes && !isFileTypeValid(mimeTypes, file)) {
        toast({
          description: `${label ?? 'File'} must be in ${mimeTypes.join(
            ', ',
          )} format`,
          status: 'error',
          isClosable: true,
        });
        return false;
      }

      if (maxSize && isMaxFileSize(maxSize, file)) {
        const unitSize = getUnit(maxSize);
        toast({
          description: `${label ?? 'File'} can't be more than ${
            unitSize.size
          } ${unitSize.unit}`,
          status: 'error',
          isClosable: true,
        });
        return false;
      }
    }

    return onChange?.(event);
  };

  return (
    <>
      <Input
        multiple={multiple}
        onChange={onChangeInput}
        type="file"
        ref={inputFileRef}
        display="none"
        accept={mimeTypes?.join(', ')}
      />
      <Button
        alt={alt}
        variant="filled"
        fontWeight="normal"
        fontSize="sm"
        color="white"
        background="primary.tealGradient"
        _hover={{ background: 'primary.tealGradientHover' }}
        isDisabled={isDisabled}
        onClick={() => {
          if (isAttachmentFull) {
            toast({
              description: `Can upload maximum ${maxAttachment} files`,
              status: 'error',
              isClosable: true,
            });
          } else {
            onClick?.() ?? inputFileRef.current?.click();
          }
        }}
        leftIcon={
          iconSrc ? (
            <Image alt="Attach file" src={iconSrc} />
          ) : (
            <PlusSquareIcon />
          )
        }
        {...props}
      >
        {label ?? 'File'}
      </Button>
    </>
  );
}
